// The client engine: the TypeScript twin of crates/hrth-client. It owns the crypto core,
// talks to the server, and keeps local state in line with what the server authorizes.
//
// Every operation that touches MLS state runs through a single queue (`exclusive`), and
// every state change is persisted (core snapshot + cursors + decrypted messages) in one
// IndexedDB transaction before the next operation starts.

import { isOpen, openRules, openTo, sealRules, type NodeRules } from '../lib/nodeShare'
import { mayRotateKey } from '../lib/roles'
import { Api, ApiError, inviteCode, inviteHost } from '../api/http'
import type {
  CommitRequest,
  ConversationInfo,
  DeviceRef,
  Envelope,
  LibraryInfo,
  LibraryKind,
  LogEntry,
  StorageLocation,
  MembershipDelta,
  Role,
  SpaceInfo,
  SystemEvent,
  ServerFrame,
  SyncResponse,
  UserId,
  Uuid,
} from '../api/types'
import { Core, CoreError, newConversationId, recovery, type Incoming } from '../core/wasm'
import { ServerClock } from '../lib/party'
import {
  openBucket,
  openRead,
  rememberRead,
  rememberWrite,
  openWrite,
  replaceKeys,
  sealBucket,
  sealLibraryStorage,
  type AccessKey,
  type BucketRecord,
  type ReadRecord,
  type WriteRecord,
} from '../lib/sealedStorage'
import {
  cleanDeviceName,
  decryptName,
  encryptName,
  generateSpaceKey,
  isEncrypted,
  keyFromInvite,
  libraryName,
  nameFromLink,
  opensLibraryName,
  unwrapLibraryKey,
  unwrapNodeLibraryKey,
  withKey,
  wrapLibraryKey,
} from '../lib/metacrypto'
import type { MediaKeyInfo } from '../lib/attachments'
import type { Storage, StoredMessage, StoredSession } from '../storage/db'
import { serverBase } from '../platform'

const MAX_RETRIES = 30

/** Exponential backoff with jitter between epoch-race retries (see hrth-client). */
function backoff(attempt: number): Promise<void> {
  const base = 15 * 2 ** Math.min(attempt, 6)
  return new Promise((r) => setTimeout(r, base / 2 + Math.random() * base))
}
const KEY_PACKAGE_LOW_WATER = 10
const KEY_PACKAGE_BATCH = 20

/** A library as shown: its name decrypted, or a placeholder when this device has no key. */
export type OpenLibrary = LibraryInfo & { locked: boolean }

export type EngineEvent =
  | { type: 'sync'; sync: SyncResponse }
  | { type: 'messages'; conv: Uuid; messages: StoredMessage[] }
  | { type: 'membership'; conv: Uuid; incoming: Extract<Incoming, { kind: 'commit' }> }
  | { type: 'warning'; message: string }
  | { type: 'device_names' }

/** One of my devices' names, and when it was chosen: the latest choice wins. */
export interface DeviceName {
  name: string
  at: number
}

type Listener = (e: EngineEvent) => void

/** A decrypted channel colour, only if it's a plain `#rrggbb`. */
export const hexColor = (c: string | null): string | null => (c && /^#[0-9a-f]{6}$/i.test(c) ? c : null)

export class Engine {
  private queue: Promise<unknown> = Promise.resolve()
  private listeners = new Set<Listener>()
  private cursors: Record<string, number>
  /** Last sync with space/channel names decrypted for display. */
  lastSync: SyncResponse | null = null
  private rawSync: SyncResponse | null = null
  /** Space metadata keys (space id → key), shared via invite fragments and the self-group. */
  private spaceKeys: Record<string, string> = {}
  /** Library keys (library id → key): unwrapped from space wraps, or sent by DM or self-group. */
  private libraryKeys: Record<string, string> = {}
  /** Keys of my own sealed buckets (location id → key), synced through my self-group only. */
  private storageKeys: Record<string, string> = {}
  private keysLoaded = false
  private shareKeysPending = false
  /** Channels where someone new arrived and (as space owner) I should hand out the name key. */
  private keyRepostPending = new Set<Uuid>()
  /** Names of my devices (device id → name), synced through my self-group; the server never sees them. */
  private deviceNames: Record<string, DeviceName> = {}
  /** Group DM names (conversation → name, and the log seq that set it), from conv_name messages. */
  private dmNames: Record<string, { name: string | null; seq: number }> = {}
  /** Group DMs where someone joined by themselves and I'm the one to tell them the name. */
  private namePostPending = new Set<Uuid>()
  /** Estimates the server's clock from log timestamps (for synced playback). */
  readonly clock = new ServerClock()

  private constructor(
    readonly api: Api,
    private core: Core,
    private storage: Storage,
    session: StoredSession,
  ) {
    this.cursors = { ...session.cursors }
    api.token = session.token
  }

  get userId(): UserId {
    return this.core.userId
  }

  get deviceId(): Uuid {
    return this.core.deviceId
  }

  get token(): string | null {
    return this.api.token
  }

  on(l: Listener): () => void {
    this.listeners.add(l)
    return () => this.listeners.delete(l)
  }

  private emit(e: EngineEvent): void {
    for (const l of this.listeners) l(e)
  }

  /** Serializes operations on the (single-threaded) MLS state. */
  private exclusive<T>(f: () => Promise<T>): Promise<T> {
    const run = this.queue.then(f, f)
    this.queue = run.catch(() => undefined)
    return run
  }

  private session(): StoredSession {
    return {
      userId: this.core.userId,
      deviceId: this.core.deviceId,
      token: this.api.token ?? '',
      snapshot: this.core.snapshot(),
      cursors: { ...this.cursors },
    }
  }

  private persist(messages: StoredMessage[] = []): Promise<void> {
    return this.storage.save(this.session(), messages)
  }

  // ---------------------------------------------------------------------------------
  // Lifecycle

  /** Signal-style sign-up: invite + name, keys generated on this device. */
  static async register(
    api: Api,
    storage: Storage,
    invite: string,
    username: string,
    displayName: string,
  ): Promise<{ engine: Engine; spaceId: Uuid | null }> {
    const info = await api.instance()
    const core = Core.generate(`${username}@${info.domain}`)
    const resp = await api.register({
      invite_code: inviteCode(invite),
      username,
      display_name: displayName,
      device_id: core.deviceId,
      public_key: core.publicKey,
      signature: core.registerSignature(),
    })
    api.token = resp.token
    const engine = new Engine(api, core, storage, {
      userId: resp.user_id,
      deviceId: resp.device_id,
      token: resp.token,
      snapshot: core.snapshot(),
      cursors: {},
    })
    // Persist identity first: losing it after registering would orphan the account.
    await engine.persist()
    await api.uploadKeyPackages(core.keyPackageUpload(KEY_PACKAGE_BATCH, true))
    const k = keyFromInvite(invite)
    if (resp.space_id && k) await engine.storeKey(resp.space_id, k)
    return { engine, spaceId: resp.space_id }
  }

  /** Adds this browser as a new device of an existing account (link from another device). */
  static async link(api: Api, storage: Storage, linkUrlOrToken: string): Promise<Engine> {
    const token = linkToken(linkUrlOrToken)
    const preview = await api.linkPreview(token)
    const core = Core.generate(preview.profile.user_id)
    const resp = await api.linkDevice(token, core.deviceId, core.publicKey, core.registerSignature())
    const engine = await Engine.adopt(api, storage, core, resp.token)
    // The name chosen on the device that made the link rides in its fragment.
    const name = nameFromLink(linkUrlOrToken)
    if (name) await engine.exclusive(() => engine.storeDeviceName(core.deviceId, name, Date.now()))
    return engine
  }

  /** Recovers an account onto this browser with its recovery code. */
  static async recover(api: Api, storage: Storage, userId: UserId, code: string): Promise<Engine> {
    const proof = recovery.proof(code)
    if (!proof) throw new Error('That doesn’t look like a recovery code. It has 32 letters and numbers.')
    const core = Core.generate(userId)
    const resp = await api.recover(userId, proof, core.deviceId, core.publicKey, core.registerSignature())
    return Engine.adopt(api, storage, core, resp.token)
  }

  private static async adopt(api: Api, storage: Storage, core: Core, token: string): Promise<Engine> {
    api.token = token
    const engine = new Engine(api, core, storage, {
      userId: core.userId,
      deviceId: core.deviceId,
      token,
      snapshot: core.snapshot(),
      cursors: {},
    })
    await engine.persist()
    await api.uploadKeyPackages(core.keyPackageUpload(KEY_PACKAGE_BATCH, true))
    return engine
  }

  /** Creates a recovery code, registers its verifier, and returns the code (show once). */
  async enableRecovery(): Promise<string> {
    const code = recovery.generate()
    const proof = recovery.proof(code)!
    await this.api.setRecovery(recovery.verifier(proof))
    return code
  }

  /** Restores a saved session; re-authenticates with the device key if the token died. */
  static async resume(api: Api, storage: Storage): Promise<Engine | null> {
    const session = await storage.loadSession()
    if (!session) return null
    const core = Core.restore(session.snapshot)
    const engine = new Engine(api, core, storage, session)
    try {
      await api.me()
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        await engine.login()
      } else if (!(e instanceof ApiError && e.code === 'transport')) {
        throw e
      }
    }
    return engine
  }

  /** Challenge/response login with the device key. */
  async login(): Promise<void> {
    const { nonce } = await this.api.challenge(this.core.userId, this.core.deviceId)
    const { token } = await this.api.login(this.core.userId, this.core.deviceId, nonce, this.core.loginSignature(nonce))
    this.api.token = token
    await this.persist()
  }

  /** Removes stored messages from this device only (Delete from device). */
  deleteLocal(conv: Uuid, seqs: number[]): Promise<void> {
    return this.storage.deleteMessages(conv, seqs)
  }

  /** Forgets this device locally (the account remains on the server). */
  async signOut(): Promise<void> {
    await this.exclusive(async () => {
      try {
        await this.api.logout()
      } catch {
        // best effort
      }
      await this.storage.clear()
      await import('../lib/attachmentStore').then((s) => s.forgetAttachments()).catch(() => undefined)
      this.core.free()
    })
  }

  // ---------------------------------------------------------------------------------
  // Commits

  private async submitCommit(conv: Uuid, req: CommitRequest): Promise<number> {
    const external = req.delta.external_join
    try {
      const r = await this.api.commit(conv, req)
      this.core.confirmCommit(conv)
      const cur = this.cursors[conv] ?? 0
      if (external || cur + 1 === r.seq) this.cursors[conv] = r.seq
      // Notices for changes we made ourselves (others see them via the log). Our own
      // arrival isn't news to us.
      // (The first commit just populates a brand-new group: not news.)
      const notices = external || req.epoch === 0 ? [] : this.notices(conv, r.seq, Date.now(), req.delta, null)
      await this.persist(notices)
      if (notices.length) this.emit({ type: 'messages', conv, messages: notices })
      return r.seq
    } catch (e) {
      if (external) this.core.forgetGroup(conv)
      else this.core.abortCommit(conv)
      throw e
    }
  }

  private async joinExternal(conv: Uuid): Promise<void> {
    for (let i = 0; i < MAX_RETRIES; i++) {
      const gi = await this.api.groupInfo(conv)
      const req = this.core.joinExternal(conv, gi.group_info)
      this.cursors[conv] = gi.last_seq
      try {
        await this.submitCommit(conv, req)
        return
      } catch (e) {
        if (e instanceof ApiError && e.is('epoch_mismatch')) {
          await backoff(i)
          continue
        }
        delete this.cursors[conv]
        throw e
      }
    }
    throw new Error('could not join conversation')
  }

  private async removeUnauthorized(conv: ConversationInfo, sync: SyncResponse): Promise<void> {
    const authorized = devicesOf(sync, conv.members)
    for (let i = 0; i < MAX_RETRIES; i++) {
      if (!this.core.hasGroup(conv.id)) return
      // Never treat a member's devices as removed when the sync carried no device info
      // for them (e.g. an older server).
      const known = new Set((sync.devices ?? []).map((u) => u.user_id))
      const stale = this.core
        .unauthorizedDevices(conv.id, authorized)
        .filter((d) => !conv.members.includes(d.user_id) || known.has(d.user_id))
      if (stale.length === 0) return
      try {
        await this.submitCommit(conv.id, this.core.removeDevices(conv.id, stale))
        return
      } catch (e) {
        if (e instanceof ApiError && e.is('epoch_mismatch')) {
          await backoff(i)
          await this.pullLocked(conv.id)
          continue
        }
        // Our sync predates a newly linked device; the next reconcile has fresh data.
        if (e instanceof ApiError && e.status === 403) return
        throw e
      }
    }
  }

  private async processWelcomes(): Promise<void> {
    const { welcomes } = await this.api.welcomes()
    for (const w of welcomes) {
      try {
        const conv = this.core.joinWelcome(w.welcome)
        this.cursors[conv] = w.seq
        await this.persist()
      } catch (e) {
        // Already joined (e.g. by external commit) or stale: drop it.
        if (!(e instanceof CoreError && (e.code === 'group_exists' || e.code === 'mls'))) throw e
      }
      await this.api.ackWelcome(w.id)
    }
  }

  // ---------------------------------------------------------------------------------
  // Pull & reconcile

  /** Processes new log entries for one conversation (caller holds the lock). */
  private async pullLocked(conv: Uuid): Promise<void> {
    for (;;) {
      if (!this.core.hasGroup(conv)) return
      const page = await this.api.log(conv, this.cursors[conv] ?? 0)
      if (page.missed) {
        await this.rejoinAfterGap(conv)
        continue
      }
      const fresh: StoredMessage[] = []
      let removed = false
      const now = Date.now()
      for (const entry of page.entries) {
        this.clock.observe(entry.created_at, now)
        const incoming = this.processEntry(conv, entry)
        this.cursors[conv] = entry.seq
        if (incoming.kind === 'message') {
          const env = incoming.envelope
          if (env.type === 'space_key') await this.acceptSpaceKey(conv, incoming.sender.user_id, env.space, env.key)
          if (env.type === 'library_key') await this.acceptLibraryKey(env.library, env.key)
          if (env.type === 'storage_key') await this.acceptStorageKey(conv, incoming.sender.user_id, env.location, env.key)
          if (env.type === 'device_name' && conv === this.selfConv() && incoming.sender.user_id === this.userId) {
            const name = cleanDeviceName(env.name)
            // Not news to pass on: whoever sent it already told all my devices.
            const pending = this.shareKeysPending
            if (name) await this.storeDeviceName(env.device, name, env.at)
            this.shareKeysPending = pending
          }
          if (env.type === 'conv_name' && this.isGroupDm(conv)) await this.noteDmName(conv, entry.seq, env.name)
          fresh.push({
            conv,
            seq: entry.seq,
            sender: incoming.sender,
            envelope: incoming.envelope,
            receivedAt: entry.created_at,
          })
        } else if (incoming.kind === 'commit') {
          if (incoming.delta_mismatch) {
            this.emit({ type: 'warning', message: 'A membership change did not match what the server announced.' })
          }
          this.emit({ type: 'membership', conv, incoming })
          if (incoming.removed_self) {
            removed = true
            break
          }
          // A device rejoining as itself after a gap isn't news.
          const notices = incoming.resync ? [] : this.notices(conv, entry.seq, entry.created_at, incoming.actual, incoming.sender)
          fresh.push(...notices)
          // Someone joined a named group by themselves: they can't read the name, so the
          // first of those already in it (by user id) tells them. Whoever adds people does.
          const joined = notices.flatMap((n) => (n.envelope.type === 'system' && n.envelope.event === 'joined' ? [n.envelope.user] : []))
          if (joined.includes(incoming.sender?.user_id ?? '') && this.isGroupDm(conv) && this.dmNames[conv]?.name) {
            const stayed = [...new Set(this.core.members(conv).map((d) => d.user_id))].filter((u) => !joined.includes(u)).sort()
            if (stayed[0] === this.userId) this.namePostPending.add(conv)
          }
          // One of my other devices joined my self-group: give it the space keys.
          if (conv === this.selfConv() && incoming.actual.adds.some((d) => d.user_id === this.userId)) {
            this.shareKeysPending = true
          }
          // Someone joined a channel of a space I own: they may hold an outdated name key
          // (invite links created before a rotation), so hand them the current one.
          if (incoming.actual.adds.some((d) => d.user_id !== this.userId)) {
            const sp = this.spaceOfChannel(conv)
            if (sp?.my_role === 'owner' && this.spaceKeys[sp.id]) this.keyRepostPending.add(conv)
          }
        }
      }
      if (removed) delete this.cursors[conv]
      // State, cursor and plaintext land together, or not at all.
      await this.persist(fresh)
      if (fresh.length) this.emit({ type: 'messages', conv, messages: fresh })
      if (removed || !page.more) return
    }
  }

  /**
   * What this device hadn't fetched is gone from the server (kept 30 days at most,
   * HRTH-109): rejoin with the latest GroupInfo, as the same device, and say so in the
   * conversation.
   */
  private async rejoinAfterGap(conv: Uuid): Promise<void> {
    this.core.forgetGroup(conv)
    delete this.cursors[conv]
    await this.joinExternal(conv)
    const seq = this.cursors[conv] ?? 0
    const now = Date.now()
    const notice: StoredMessage = {
      conv,
      seq: seq + 0.0005,
      sender: { user_id: this.userId, device_id: '' },
      envelope: { v: 1, id: `sys-${seq}-missed`, ts: now, type: 'system', event: 'missed', user: this.userId },
      receivedAt: now,
    }
    await this.persist([notice])
    this.emit({ type: 'messages', conv, messages: [notice] })
  }

  private processEntry(conv: Uuid, entry: LogEntry): Incoming {
    try {
      return this.core.process(conv, entry)
    } catch (e) {
      // A message we can't process (corrupt or from a broken client) must not wedge the
      // conversation; skip it and tell the user.
      this.emit({ type: 'warning', message: `Could not decrypt a message: ${(e as Error).message}` })
      return { kind: 'skipped' }
    }
  }

  private notices(
    conv: Uuid,
    seq: number,
    ts: number,
    delta: MembershipDelta,
    sender: DeviceRef | null,
  ): StoredMessage[] {
    const after = this.core.hasGroup(conv) ? this.core.members(conv) : []
    return membershipNotices(delta, after).map((n, i) => ({
      conv,
      // Notices sort just after the commit that caused them.
      seq: seq + (i + 1) / 1000,
      sender: sender ?? { user_id: n.user, device_id: '' },
      envelope: { v: 1, id: `sys-${seq}-${i}`, ts, type: 'system', event: n.event, user: n.user },
      receivedAt: ts,
    }))
  }

  pull(conv: Uuid): Promise<void> {
    return this.exclusive(async () => {
      await this.pullLocked(conv)
      // A new device of mine just joined my self-group: hand it the space keys now.
      if (this.shareKeysPending && this.rawSync) await this.shareAllKeys()
      await this.repostKeys()
    })
  }

  /** Makes local MLS state match server authorization. Idempotent. */
  reconcile(): Promise<SyncResponse> {
    return this.exclusive(async () => {
      await this.loadKeys()
      await this.processWelcomes()
      const sync = await this.api.sync()
      this.rawSync = sync
      const convs = allConversations(sync)
      for (const c of convs) {
        if (this.core.hasGroup(c.id)) {
          await this.pullLocked(c.id)
        } else {
          try {
            await this.joinExternal(c.id)
          } catch (e) {
            if (e instanceof ApiError && e.is('already_member')) {
              await this.processWelcomes()
              continue
            }
            this.emit({ type: 'warning', message: `Could not join ${c.name ?? 'a conversation'}: ${(e as Error).message}` })
            continue
          }
        }
        await this.removeUnauthorized(c, sync)
      }
      const authorized = new Set(convs.map((c) => c.id))
      for (const conv of this.core.conversations()) {
        if (!authorized.has(conv)) {
          this.core.forgetGroup(conv)
          delete this.cursors[conv]
          await this.storage.deleteConversation(conv)
        }
      }
      await this.persist()
      await this.ensureSelfGroup(sync)
      if (this.shareKeysPending) await this.shareAllKeys()
      await this.repostKeys()
      await this.topUpKeyPackages()
      return this.publishSync()
    })
  }

  // ---------------------------------------------------------------------------------
  // Encrypted names: space keys and the private self-group

  private async loadKeys(): Promise<void> {
    if (this.keysLoaded) return
    this.spaceKeys = { ...((await this.storage.getKv<Record<string, string>>('spaceKeys')) ?? {}), ...this.spaceKeys }
    this.libraryKeys = {
      ...((await this.storage.getKv<Record<string, string>>('libraryKeys')) ?? {}),
      ...this.libraryKeys,
    }
    this.storageKeys = {
      ...((await this.storage.getKv<Record<string, string>>('storageKeys')) ?? {}),
      ...this.storageKeys,
    }
    this.dmNames = { ...((await this.storage.getKv<Engine['dmNames']>('dmNames')) ?? {}), ...this.dmNames }
    this.deviceNames = { ...((await this.storage.getKv<Engine['deviceNames']>('deviceNames')) ?? {}), ...this.deviceNames }
    this.keysLoaded = true
  }

  /** Records a space key; shares it with my other devices if it's new. */
  private async storeKey(space: Uuid, key: string): Promise<void> {
    await this.loadKeys()
    if (this.spaceKeys[space] === key) return
    this.spaceKeys = { ...this.spaceKeys, [space]: key }
    await this.storage.setKv('spaceKeys', this.spaceKeys)
    this.shareKeysPending = true
    // Names may have just become readable.
    if (this.rawSync) void this.publishSync()
  }

  /** Records a library key; shares it with my other devices if it's new. */
  private async storeLibraryKey(library: string, key: string): Promise<void> {
    await this.loadKeys()
    if (this.libraryKeys[library] === key) return
    this.libraryKeys = { ...this.libraryKeys, [library]: key }
    await this.storage.setKv('libraryKeys', this.libraryKeys)
    this.shareKeysPending = true
  }

  /** Records one of my sealed buckets' keys; shares it with my other devices if it's new. */
  private async storeStorageKey(location: string, key: string): Promise<void> {
    await this.loadKeys()
    if (this.storageKeys[location] === key) return
    this.storageKeys = { ...this.storageKeys, [location]: key }
    await this.storage.setKv('storageKeys', this.storageKeys)
    this.shareKeysPending = true
  }

  /**
   * A sealed bucket's key from one of my own devices (self-group only). Kept only if it opens
   * that bucket's record, so nobody can plant a wrong key.
   */
  private async acceptStorageKey(conv: Uuid, sender: UserId, location: string, key: string): Promise<void> {
    if (conv !== this.selfConv() || sender !== this.userId) return
    await this.loadKeys()
    if (this.storageKeys[location] === key) return
    let sealed: string | undefined
    try {
      sealed = (await this.api.myStorage()).locations.find((l) => l.id === location)?.sealed
    } catch {
      return
    }
    if (await openBucket(key, location, sealed)) await this.storeStorageKey(location, key)
  }

  /**
   * Keeps a device name if it's newer than the one I have (ties go to the larger name, so every
   * device settles on the same one), as hrth-client does. Returns whether it was kept.
   */
  private async storeDeviceName(device: Uuid, name: string, at: number): Promise<boolean> {
    await this.loadKeys()
    const old = this.deviceNames[device]
    if (old && (at < old.at || (at === old.at && name <= old.name))) return false
    this.deviceNames = { ...this.deviceNames, [device]: { name, at } }
    await this.storage.setKv('deviceNames', this.deviceNames)
    this.shareKeysPending = true
    this.emit({ type: 'device_names' })
    return true
  }

  /** The names of my devices (device id → name). */
  deviceNamesById(): Record<string, string> {
    return Object.fromEntries(Object.entries(this.deviceNames).map(([d, n]) => [d, n.name]))
  }

  /** Names (or renames) one of my devices, this one included, and tells my other devices. */
  renameDevice(device: Uuid, raw: string): Promise<void> {
    return this.exclusive(async () => {
      const name = cleanDeviceName(raw)
      if (!name) throw new Error('Device names are 1–64 printable characters.')
      const at = Date.now()
      const pending = this.shareKeysPending
      await this.storeDeviceName(device, name, at)
      const conv = this.selfConv()
      if (conv && this.core.hasGroup(conv)) {
        await this.sendLocked(conv, { v: 1, id: newConversationId(), ts: at, type: 'device_name', device, name, at })
        // Sent: no need to re-send everything for it.
        this.shareKeysPending = pending
      }
    })
  }

  /**
   * A library key arriving in a DM (shared with me personally) or from my own devices. Kept
   * only if it opens the library's encrypted name, so nobody can plant a wrong key.
   */
  private async acceptLibraryKey(library: string, key: string): Promise<void> {
    await this.loadKeys()
    if (this.libraryKeys[library] === key) return
    let lib: LibraryInfo | undefined
    try {
      lib = (await this.api.libraries()).find((l) => l.id === library)
    } catch {
      return
    }
    if (lib && (await opensLibraryName(key, library, lib.name))) await this.storeLibraryKey(library, key)
  }

  private spaceOfChannel(conv: Uuid): SpaceInfo | undefined {
    return this.rawSync?.spaces.find((sp) => sp.channels.some((c) => c.id === conv))
  }

  /**
   * A space key arriving in a conversation. Trusted from my own devices (self-group) and,
   * inside a channel of that same space, from someone who may rotate it (the owner, or with Remove members).
   */
  private async acceptSpaceKey(conv: Uuid, sender: UserId, space: Uuid, key: string): Promise<void> {
    if (conv === this.selfConv()) {
      if (sender === this.userId) await this.storeKey(space, key)
      return
    }
    const sp = this.spaceOfChannel(conv)
    if (sp?.id !== space) return
    if (mayRotateKey(sp, sender)) await this.storeKey(space, key)
  }

  /** Sends the current space key into channels that gained members (caller holds the lock). */
  private async repostKeys(): Promise<void> {
    for (const conv of [...this.namePostPending]) {
      this.namePostPending.delete(conv)
      await this.repostDmName(conv)
    }
    for (const conv of [...this.keyRepostPending]) {
      this.keyRepostPending.delete(conv)
      const sp = this.spaceOfChannel(conv)
      const key = sp && this.spaceKeys[sp.id]
      if (!key || !this.core.hasGroup(conv)) continue
      try {
        await this.sendLocked(conv, { v: 1, id: newConversationId(), ts: Date.now(), type: 'space_key', space: sp.id, key })
      } catch (e) {
        this.emit({ type: 'warning', message: `Could not share the space key: ${(e as Error).message}` })
      }
    }
  }

  /**
   * Replaces a space's name key (after removing someone): re-encrypts the names and hands
   * the new key to the remaining members through each channel's MLS group, which the
   * removed person is no longer part of (caller holds the lock, after reconciling).
   */
  private async rotateSpaceKeyLocked(space: Uuid, removed: UserId): Promise<void> {
    const sp = this.rawSync?.spaces.find((s) => s.id === space)
    const old = this.spaceKeys[space]
    if (!sp || !old || !isEncrypted(sp.name)) return
    const name = await decryptName(old, 'space', sp.name)
    if (name === null) return
    // Only channels the removed person can no longer read get the key.
    const channels = sp.channels.filter((c) => this.core.hasGroup(c.id))
    if (channels.some((c) => this.core.members(c.id).some((d) => d.user_id === removed))) {
      this.emit({ type: 'warning', message: 'Could not rotate the space key yet: removal is still in progress.' })
      return
    }
    const key = generateSpaceKey()
    await this.storeKey(space, key)
    for (const c of channels) {
      await this.sendLocked(c.id, { v: 1, id: newConversationId(), ts: Date.now(), type: 'space_key', space, key })
    }
    // Libraries shared with the space: their keys were wrapped under the old key.
    try {
      for (const l of await this.api.libraries()) {
        const w = l.key_wraps.find((x) => x.space === space)
        const k = w && (await unwrapLibraryKey(old, l.id, w.wrapped))
        if (k) await this.api.rewrapLibrary(l.id, space, await wrapLibraryKey(key, l.id, k))
      }
    } catch (e) {
      this.emit({ type: 'warning', message: `Could not re-share library keys: ${(e as Error).message}` })
    }
    await this.api.renameSpace(space, await encryptName(key, 'space', name))
    // The symbol too, so the removed person can't read a new one.
    const icon = sp.icon && isEncrypted(sp.icon) ? await decryptName(old, 'icon', sp.icon) : null
    if (icon !== null) await this.api.setSpaceIcon(space, await encryptName(key, 'icon', icon))
    for (const c of sp.channels) {
      const plain = c.name ? await decryptName(old, 'channel', c.name) : null
      if (plain !== null && isEncrypted(c.name)) await this.api.renameChannel(space, c.id, await encryptName(key, 'channel', plain))
      const color = c.color && isEncrypted(c.color) ? await decryptName(old, 'color', c.color) : null
      if (color !== null) await this.api.setChannelColor(space, c.id, await encryptName(key, 'color', color))
    }
  }

  spaceKey(space: Uuid): string | undefined {
    return this.spaceKeys[space]
  }

  /**
   * My private group with only my own devices: marked by the server, so a group DM the others
   * have left never counts (older servers: a DM whose only member is me).
   */
  private selfConv(sync = this.rawSync): Uuid | null {
    const alone = (c: ConversationInfo) => c.members.length === 1 && c.members[0] === this.userId
    const mine = (sync?.conversations ?? [])
      .filter((c) => c.kind === 'dm' && !c.host && (c.self_group ?? alone(c)))
      .map((c) => c.id)
      .sort()
    return mine[0] ?? null
  }

  private async ensureSelfGroup(sync: SyncResponse): Promise<void> {
    if (this.selfConv(sync)) return
    const conv = newConversationId()
    const gi = this.core.createGroup(conv)
    try {
      const info = await this.api.createDm(conv, [], gi)
      sync.conversations.push(info)
      this.cursors[conv] = 0
      await this.persist()
      this.shareKeysPending = true
    } catch (e) {
      this.core.forgetGroup(conv)
      this.emit({ type: 'warning', message: `Could not set up device sync: ${(e as Error).message}` })
    }
  }

  /** Sends every space key I hold to my self-group (for my other devices). */
  private async shareAllKeys(): Promise<void> {
    const conv = this.selfConv()
    if (!conv || !this.core.hasGroup(conv)) return
    this.shareKeysPending = false
    const mine = new Set((this.rawSync?.devices.find((u) => u.user_id === this.userId)?.devices ?? []).map((d) => d.device_id))
    const envelopes: Envelope[] = [
      ...Object.entries(this.spaceKeys).map(
        ([space, key]): Envelope => ({ v: 1, id: newConversationId(), ts: Date.now(), type: 'space_key', space, key }),
      ),
      ...Object.entries(this.libraryKeys).map(
        ([library, key]): Envelope => ({ v: 1, id: newConversationId(), ts: Date.now(), type: 'library_key', library, key }),
      ),
      ...Object.entries(this.storageKeys).map(
        ([location, key]): Envelope => ({ v: 1, id: newConversationId(), ts: Date.now(), type: 'storage_key', location, key }),
      ),
      // Names of devices that are still mine.
      ...Object.entries(this.deviceNames)
        .filter(([device]) => mine.has(device))
        .map(([device, n]): Envelope => ({ v: 1, id: newConversationId(), ts: Date.now(), type: 'device_name', device, name: n.name, at: n.at })),
    ]
    for (const env of envelopes) {
      try {
        await this.sendLocked(conv, env)
      } catch (e) {
        this.shareKeysPending = true
        this.emit({ type: 'warning', message: `Could not sync keys: ${(e as Error).message}` })
        return
      }
    }
  }

  /** Emits the last sync with names decrypted (placeholders where no key is known). */
  private async publishSync(): Promise<SyncResponse> {
    const raw = this.rawSync!
    const spaces = await Promise.all(
      raw.spaces.map(async (sp) => {
        const k = this.spaceKeys[sp.id]
        return {
          ...sp,
          name: (await decryptName(k, 'space', sp.name)) ?? '🔒 Private space',
          icon: sp.icon ? ((await decryptName(k, 'icon', sp.icon)) ?? undefined) : undefined,
          channels: await Promise.all(
            sp.channels.map(async (c) => ({
              ...c,
              name: c.name ? ((await decryptName(k, 'channel', c.name)) ?? '🔒 channel') : c.name,
              // Goes into CSS, so only a plain #rrggbb survives.
              color: c.color && isEncrypted(c.color) ? (hexColor(await decryptName(k, 'color', c.color)) ?? undefined) : undefined,
            })),
          ),
        }
      }),
    )
    const self = this.selfConv()
    const conversations = raw.conversations
      .filter((c) => c.id !== self)
      // A group's name lives in its messages; the server never has it.
      .map((c) => (c.kind === 'dm' && c.group ? { ...c, name: this.dmNames[c.id]?.name ?? null } : c))
    const sync: SyncResponse = { ...raw, spaces, conversations }
    this.lastSync = sync
    this.emit({ type: 'sync', sync })
    return sync
  }

  private async topUpKeyPackages(): Promise<void> {
    const me = await this.api.me()
    if (me.key_packages_remaining < KEY_PACKAGE_LOW_WATER) {
      await this.api.uploadKeyPackages(this.core.keyPackageUpload(KEY_PACKAGE_BATCH, false))
    }
  }

  /** Routes a WebSocket notification. */
  handleFrame(f: ServerFrame): Promise<unknown> {
    switch (f.t) {
      case 'log':
        if ((this.cursors[f.conversation_id] ?? 0) >= f.seq) return Promise.resolve()
        return this.pull(f.conversation_id)
      case 'welcome':
      case 'space':
      case 'conversations':
        return this.reconcile()
      default:
        return Promise.resolve()
    }
  }

  // ---------------------------------------------------------------------------------
  // Actions

  sendText(conv: Uuid, text: string, replyTo?: Uuid, mentions?: UserId[]): Promise<StoredMessage> {
    if (mentions?.length) {
      const envelope: Envelope = { v: 1, id: newConversationId(), ts: Date.now(), type: 'text', body: text, mentions, ...(replyTo ? { reply_to: replyTo } : {}) }
      return this.sendEnvelope(conv, envelope)
    }
    return this.exclusive(async () => {
      for (let i = 0; i < MAX_RETRIES; i++) {
        const enc = this.core.encryptText(conv, text, replyTo)
        try {
          const { seq } = await this.api.sendMessage(conv, enc.epoch, enc.message, enc.commitment)
          return await this.recordOwn(conv, seq, enc.envelope)
        } catch (e) {
          if (e instanceof ApiError && e.is('epoch_mismatch')) {
            await this.pullLocked(conv)
            continue
          }
          throw e
        }
      }
      throw new Error('could not send: the conversation kept changing')
    })
  }

  sendEnvelope(conv: Uuid, envelope: Envelope): Promise<StoredMessage> {
    return this.exclusive(() => this.sendLocked(conv, envelope))
  }

  private async sendLocked(conv: Uuid, envelope: Envelope): Promise<StoredMessage> {
    for (let i = 0; i < MAX_RETRIES; i++) {
      const enc = this.core.encrypt(conv, envelope)
      const mentions = envelope.type === 'text' ? envelope.mentions : undefined
      try {
        const { seq } = await this.api.sendMessage(conv, enc.epoch, enc.message, enc.commitment, mentions)
        return await this.recordOwn(conv, seq, enc.envelope)
      } catch (e) {
        if (e instanceof ApiError && e.is('epoch_mismatch')) {
          await this.pullLocked(conv)
          continue
        }
        throw e
      }
    }
    throw new Error('could not send: the conversation kept changing')
  }

  /** Encrypts, uploads and shares a file (image/audio/video/anything). */
  async sendFile(
    conv: Uuid,
    file: File,
    opts: { caption?: string; onProgress?: (stage: 'encrypting' | 'uploading', fraction: number) => void } = {},
  ): Promise<StoredMessage> {
    const { encryptFile, fileEncryptor, uploadResumable } = await import('../lib/attachments')
    const { uploadCiphertext, imageThumbnail, mediaKind } = await import('../lib/media')
    let info: MediaKeyInfo
    let up: { id: string; size: number }
    if (this.hostOf(conv) === this.userId.split('@')[1]) {
      // Our server: encrypt and upload part by part (bounded memory, resumable).
      const enc = await fileEncryptor(file)
      info = enc.info
      const api = this.api
      up = await uploadResumable(
        {
          startUpload: (size) => api.startUpload(conv, size),
          uploadPart: (id, offset, part) => api.uploadPart(id, offset, part),
          uploadStatus: (id) => api.uploadStatus(id),
          completeUpload: (id) => api.completeUpload(id),
          cancelUpload: (id) => api.cancelUpload(id),
        },
        enc,
        { onProgress: (f) => opts.onProgress?.('uploading', f) },
      )
    } else {
      // Hosted elsewhere (federation): single request through our server's proxy.
      const r = await encryptFile(file, undefined, (f) => opts.onProgress?.('encrypting', f))
      info = r.info
      up = await uploadCiphertext(this.api.token ?? '', conv, r.ciphertext, (f) => opts.onProgress?.('uploading', f))
    }
    // The sender keeps what they sent: the server's copy is only for the others.
    await import('../lib/attachmentStore').then((s) => s.keepLocally(up.id, file)).catch(() => undefined)
    const mime = file.type || 'application/octet-stream'
    const thumb = mediaKind(mime) === 'image' ? await imageThumbnail(file) : null
    const envelope: Envelope = {
      v: 1,
      id: newConversationId(),
      ts: Date.now(),
      type: 'media',
      media: {
        // Where the ciphertext lives, so members on other servers can fetch it.
        server: this.hostOf(conv),
        id: up.id,
        ...info,
        mime,
        name: file.name || 'file',
        ...(thumb ?? {}),
      },
      ...(opts.caption ? { body: opts.caption } : {}),
    }
    return this.sendEnvelope(conv, envelope)
  }

  private async recordOwn(conv: Uuid, seq: number, envelope: Envelope): Promise<StoredMessage> {
    const msg: StoredMessage = {
      conv,
      seq,
      sender: { user_id: this.core.userId, device_id: this.core.deviceId },
      envelope,
      receivedAt: Date.now(),
    }
    const cur = this.cursors[conv] ?? 0
    if (cur + 1 === seq) this.cursors[conv] = seq
    await this.persist([msg])
    this.emit({ type: 'messages', conv, messages: [msg] })
    return msg
  }

  /** Creates a space whose name (and channel names) only members can read. */
  async createSpace(name: string): Promise<Uuid> {
    const key = generateSpaceKey()
    const space = await this.api.createSpace(await encryptName(key, 'space', name))
    await this.exclusive(() => this.storeKey(space.id, key))
    await this.createChannel(space.id, 'general')
    return space.id
  }

  createChannel(space: Uuid, name: string): Promise<Uuid> {
    return this.exclusive(async () => {
      const conv = newConversationId()
      const gi = this.core.createGroup(conv)
      let info: ConversationInfo
      const key = this.spaceKeys[space]
      try {
        info = await this.api.createChannel(space, conv, key ? await encryptName(key, 'channel', name) : name, gi)
      } catch (e) {
        this.core.forgetGroup(conv)
        throw e
      }
      this.cursors[conv] = 0
      await this.persist()
      // One commit adding everyone, instead of every member racing to join on their own.
      await this.addUsersNow(
        conv,
        info.members.filter((u) => u !== this.userId),
      ).catch((e) => this.emit({ type: 'warning', message: `Members will join shortly (${(e as Error).message})` }))
      return conv
    })
  }

  /** Adds all devices of `users` to a group we just created, in one commit (caller holds the lock). */
  private async addUsersNow(conv: Uuid, users: UserId[]): Promise<void> {
    const kps: { device: DeviceRef; kp: string }[] = []
    for (const u of users) {
      try {
        for (const c of (await this.api.claimKeyPackages(u)).key_packages) kps.push({ device: c.device, kp: c.key_package })
      } catch {
        // They'll join by external commit instead.
      }
    }
    for (let i = 0; i < MAX_RETRIES; i++) {
      const present = new Set(this.core.members(conv).map((d) => d.device_id))
      const todo = kps.filter((k) => !present.has(k.device.device_id)).map((k) => k.kp)
      if (todo.length === 0) return
      try {
        await this.submitCommit(conv, this.core.addMembers(conv, todo))
        return
      } catch (e) {
        if (e instanceof ApiError && (e.is('epoch_mismatch') || e.is('already_member'))) {
          await backoff(i)
          await this.pullLocked(conv)
          continue
        }
        throw e
      }
    }
  }

  /** Starts a DM, adding peers' devices immediately via key packages + Welcome. */
  createDm(peers: UserId[]): Promise<Uuid> {
    return this.exclusive(async () => {
      const conv = newConversationId()
      const gi = this.core.createGroup(conv)
      try {
        await this.api.createDm(conv, peers, gi)
      } catch (e) {
        this.core.forgetGroup(conv)
        throw e
      }
      this.cursors[conv] = 0
      await this.persist()
      await this.addUsersNow(conv, peers)
      return conv
    })
  }

  /** Invite link; for a space, carries its name key in the #fragment (never sent to servers). */
  async createInvite(space: Uuid | null, maxUses?: number, expiresInSecs?: number) {
    const inv = await this.api.createInvite({ space_id: space, max_uses: maxUses ?? null, expires_in_secs: expiresInSecs ?? null })
    return { ...inv, url: withKey(inv.url, space ? this.spaceKeys[space] : undefined) }
  }

  /** Accepts an invite link; links to other servers are joined via ours (federation). */
  async acceptInvite(invite: string): Promise<Uuid | null> {
    const host = inviteHost(invite)
    const own = this.userId.split('@')[1]
    const { space_id } = await this.api.acceptInvite(inviteCode(invite), host && host !== own ? host : undefined)
    const k = keyFromInvite(invite)
    if (space_id && k) await this.exclusive(() => this.storeKey(space_id, k))
    return space_id
  }

  /** Domain of the server hosting a conversation (ours unless federated). */
  hostOf(conv: Uuid): string {
    const c = this.lastSync ? allConversations(this.lastSync).find((x) => x.id === conv) : undefined
    return c?.host ?? this.userId.split('@')[1] ?? ''
  }

  // ---------------------------------------------------------------------------------
  // Libraries: end-to-end encrypted with a library key (docs/STORAGE.md §3)

  /** Libraries I can see, names decrypted; keys wrapped for my spaces are picked up here. */
  async libraries(): Promise<OpenLibrary[]> {
    await this.loadKeys()
    return Promise.all((await this.api.libraries()).map((l) => this.openLibrary(l)))
  }

  private async openLibrary(l: LibraryInfo): Promise<OpenLibrary> {
    let key: string | undefined = this.libraryKeys[l.id]
    if (key && !(await opensLibraryName(key, l.id, l.name))) key = undefined
    for (const w of key ? [] : l.key_wraps) {
      const sk = this.spaceKeys[w.space]
      const k = sk ? await unwrapLibraryKey(sk, l.id, w.wrapped) : null
      if (k && (await opensLibraryName(k, l.id, l.name))) {
        key = k
        await this.exclusive(() => this.storeLibraryKey(l.id, k))
        break
      }
    }
    // A library one of my nodes made: its key sealed under that node location's key.
    const locKey = l.location ? this.storageKeys[l.location] : undefined
    if (!key && l.node_wrap && locKey) {
      const k = await unwrapNodeLibraryKey(locKey, l.id, l.node_wrap)
      if (k && (await opensLibraryName(k, l.id, l.name))) {
        key = k
        await this.exclusive(() => this.storeLibraryKey(l.id, k))
        if (this.shareKeysPending) await this.exclusive(() => this.shareAllKeys())
      }
    }
    const name = key ? await decryptName(key, libraryName(l.id), l.name) : null
    // In a sealed bucket: remember where it is, so its files can be asked for (signed here),
    // and, for its uploaders, the key to write them.
    const read = key && l.storage ? await openRead(key, l.id, l.storage) : null
    rememberRead(l.id, read)
    rememberWrite(l.id, read, key && l.storage_write ? await openWrite(key, l.id, l.storage_write) : null)
    return { ...l, name: name ?? '🔒 Private library', locked: name === null }
  }

  /** This device's key for a library, if it has one. */
  libraryKey(library: string): string | undefined {
    return this.libraryKeys[library]
  }

  /**
   * Creates an encrypted library in a storage location; its key starts on this device. In one
   * of my sealed buckets it carries the bucket's details and keys, sealed under its key.
   */
  async createLibrary(name: string, location: string, kind: LibraryKind = 'files'): Promise<OpenLibrary> {
    await this.loadKeys()
    const key = generateSpaceKey()
    const id = `lib-${generateSpaceKey().slice(0, 16)}`
    const mine = (await this.api.myStorage()).locations.find((l) => l.id === location)
    let records = {}
    if (mine?.sealed) {
      const bucket = await this.bucket(mine)
      if (!bucket) throw new Error('This device doesn’t have that bucket’s key yet: open hrth on the device that attached it')
      records = await sealLibraryStorage(key, id, bucket)
    }
    const lib = await this.api.createLibrary({ id, name: await encryptName(key, libraryName(id), name), kind, location, ...records })
    await this.exclusive(() => this.storeLibraryKey(id, key))
    if (this.shareKeysPending) await this.exclusive(() => this.shareAllKeys())
    return this.openLibrary(lib)
  }

  // ---------------------------------------------------------------------------------
  // Buckets the server can't reach (docs/STORAGE.md §11.2)

  /** One of my sealed buckets, opened with its key (null if this device lacks it). */
  async bucket(loc: StorageLocation): Promise<BucketRecord | null> {
    await this.loadKeys()
    return openBucket(this.storageKeys[loc.id], loc.id, loc.sealed)
  }

  /** Attaches a bucket the server can't reach: only its sealed record goes to the server. */
  async attachBucket(rec: BucketRecord): Promise<StorageLocation> {
    await this.loadKeys()
    const key = generateSpaceKey()
    const id = `loc-${generateSpaceKey().slice(0, 16)}`
    const sealed = await sealBucket(key, id, rec)
    const loc = await this.api.addMyStorage({ name: '', kind: 's3', sealed, id })
    await this.exclusive(() => this.storeStorageKey(id, key))
    if (this.shareKeysPending) await this.exclusive(() => this.shareAllKeys())
    return loc
  }

  /**
   * Pairs a node of mine (docs/STORAGE.md §11.3): its location's record (its name) sealed under
   * a new key my devices keep, and the link the node claims, which carries that key past the
   * server (after #). The node is never a device: it gets only that key and makes its own
   * libraries' keys, sealed under it for my devices.
   */
  async pairNode(name: string): Promise<{ location: StorageLocation; link: string }> {
    await this.loadKeys()
    const key = generateSpaceKey()
    const id = `loc-${generateSpaceKey().slice(0, 16)}`
    const sealed = await sealBucket(key, id, { name } as BucketRecord)
    const p = await this.api.pairNode(id, sealed)
    await this.exclusive(() => this.storeStorageKey(id, key))
    if (this.shareKeysPending) await this.exclusive(() => this.shareAllKeys())
    const server = this.api.baseUrl || serverBase() || globalThis.location.origin
    return { location: p.location, link: `${server}/node-link/${p.token}#${key}` }
  }

  /** My node's upload limits for other people (HRTH-182), opened with its key. */
  async nodeRules(loc: StorageLocation): Promise<NodeRules> {
    await this.loadKeys()
    return openRules(this.storageKeys[loc.id], loc.id, loc.rules)
  }

  /** Opens my node to the people, spaces and roles `rules` names (sealed for the node); none closes it. */
  async openNode(location: string, rules: NodeRules): Promise<void> {
    await this.loadKeys()
    const key = this.storageKeys[location]
    if (!key) throw new Error('This device doesn’t have that node’s key: open hrth on the device that added it')
    const open = isOpen(rules)
    await this.api.setNodeRules(location, open ? await sealRules(key, location, rules) : null, open ? openTo(rules) : { users: [], spaces: [], roles: [] })
  }

  /**
   * Seals one of my buckets the server still holds the key of: takes its details back once,
   * seals them with the keys given (`write` absent: read-only), gives each of its libraries
   * I can open their records, and has the server forget the rest.
   */
  async sealBucket(location: string, read: AccessKey, write: AccessKey | 'current' | null): Promise<StorageLocation> {
    await this.loadKeys()
    const s = await this.api.myStorageSecret(location)
    if (write === 'current') write = { id: s.access_key_id ?? '', secret: s.secret_access_key ?? '' }
    const rec: BucketRecord = {
      name: s.name,
      ...(s.endpoint ? { endpoint: s.endpoint } : {}),
      region: s.region ?? 'us-east-1',
      bucket: s.bucket ?? '',
      prefix: s.prefix ?? '',
      read,
      ...(write ? { write } : {}),
    }
    await this.resealLibraries(location, rec)
    const key = generateSpaceKey()
    const loc = await this.api.sealMyStorage(location, await sealBucket(key, location, rec))
    await this.exclusive(() => this.storeStorageKey(location, key))
    if (this.shareKeysPending) await this.exclusive(() => this.shareAllKeys())
    return loc
  }

  /** Gives every library in one of my buckets its records for `rec`, sealed under its key. */
  private async resealLibraries(location: string, rec: BucketRecord): Promise<void> {
    for (const l of (await this.api.libraries()).filter((l) => l.location === location)) {
      const key = this.libraryKeys[l.id]
      if (!key) throw new Error('This device doesn’t have the key of every library in that bucket')
      await this.api.setLibraryStorage(l.id, await sealLibraryStorage(key, l.id, rec))
    }
  }

  /**
   * Replaces one of my sealed bucket's keys with new ones made at the provider (`write`:
   * 'current' keeps it, null makes it read-only storage): every library in it gets records
   * with the new keys, and the old ones stay in the bucket's record as retired until they're
   * revoked at the provider (docs/STORAGE.md §11.2).
   */
  async replaceBucketKeys(loc: StorageLocation, read: AccessKey, write: AccessKey | 'current' | null): Promise<StorageLocation> {
    await this.loadKeys()
    const key = this.storageKeys[loc.id]
    const rec = await openBucket(key, loc.id, loc.sealed)
    if (!key || !rec) throw new Error('This device doesn’t have that bucket’s key')
    const next = replaceKeys(rec, read, write)
    await this.resealLibraries(loc.id, next)
    return this.api.sealMyStorage(loc.id, await sealBucket(key, loc.id, next))
  }

  /** Forgets retired keys of one of my buckets (once they're revoked at the provider). */
  async forgetRetiredKeys(loc: StorageLocation, ids: string[]): Promise<StorageLocation> {
    await this.loadKeys()
    const key = this.storageKeys[loc.id]
    const rec = await openBucket(key, loc.id, loc.sealed)
    if (!key || !rec) throw new Error('This device doesn’t have that bucket’s key')
    const retired = (rec.retired ?? []).filter((k) => !ids.includes(k.id))
    const next: BucketRecord = { ...rec, ...(retired.length ? { retired } : {}) }
    if (!retired.length) delete next.retired
    return this.api.sealMyStorage(loc.id, await sealBucket(key, loc.id, next))
  }

  /** Renames one of my sealed buckets: its name is in its record, sealed again here. */
  async renameBucket(loc: StorageLocation, name: string): Promise<StorageLocation> {
    await this.loadKeys()
    const key = this.storageKeys[loc.id]
    const rec = await openBucket(key, loc.id, loc.sealed)
    if (!key || !rec) throw new Error('This device doesn’t have that bucket’s key')
    return this.api.sealMyStorage(loc.id, await sealBucket(key, loc.id, { ...rec, name }))
  }

  /** Where a library in a sealed bucket is, and its read key (null: not sealed, or no key here). */
  libraryRead(lib: LibraryInfo): Promise<ReadRecord | null> {
    return openRead(this.libraryKeys[lib.id], lib.id, lib.storage)
  }

  /** A library's write key, for its uploaders (null: none here, or read-only storage). */
  libraryWrite(lib: LibraryInfo): Promise<WriteRecord | null> {
    return openWrite(this.libraryKeys[lib.id], lib.id, lib.storage_write)
  }

  async renameLibrary(library: string, name: string): Promise<void> {
    const key = this.requireLibraryKey(library)
    await this.api.renameLibrary(library, await encryptName(key, libraryName(library), name))
  }

  private requireLibraryKey(library: string): string {
    const key = this.libraryKeys[library]
    if (!key) throw new Error('This device doesn’t have the key for that library')
    return key
  }

  /** Shares with a space (or one of its roles): the key goes along wrapped under the space key. */
  async shareLibrary(library: string, space: Uuid, role?: string): Promise<void> {
    const sk = this.spaceKeys[space]
    if (!sk) throw new Error('This device doesn’t have that space’s key')
    const wrapped = await wrapLibraryKey(sk, library, this.requireLibraryKey(library))
    if (role) await this.api.shareLibraryWithRole(library, role, wrapped)
    else await this.api.shareLibrary(library, space, wrapped)
  }

  /** Shares with one person: the server grants access, and the key goes to them in a DM. */
  async grantLibrary(library: string, user: UserId): Promise<void> {
    const key = this.requireLibraryKey(library)
    await this.api.grantLibrary(library, user)
    const dm = (this.rawSync?.conversations ?? []).find(
      (c) => c.kind === 'dm' && !c.host && !c.group && c.members.length === 2 && c.members.includes(user) && c.members.includes(this.userId),
    )
    const conv = dm?.id ?? (await this.createDm([user]))
    await this.sendEnvelope(conv, { v: 1, id: newConversationId(), ts: Date.now(), type: 'library_key', library, key })
  }

  private isGroupDm(conv: Uuid): boolean {
    return !!this.rawSync?.conversations.some((c) => c.id === conv && c.kind === 'dm' && c.group)
  }

  /** Records a group's name if it's newer than what I had. */
  private async noteDmName(conv: Uuid, seq: number, name: string | null | undefined): Promise<void> {
    if (seq <= (this.dmNames[conv]?.seq ?? -1)) return
    this.dmNames = { ...this.dmNames, [conv]: { name: cleanDmName(name), seq } }
    await this.storage.setKv('dmNames', this.dmNames)
    if (this.rawSync) void this.publishSync()
  }

  /** Names a group DM for everyone in it (null: back to its members' names). */
  renameDm(conv: Uuid, name: string | null): Promise<void> {
    return this.exclusive(async () => {
      const clean = cleanDmName(name)
      const own = await this.sendLocked(conv, { v: 1, id: newConversationId(), ts: Date.now(), type: 'conv_name', ...(clean ? { name: clean } : {}) })
      await this.noteDmName(conv, own.seq, clean)
    })
  }

  /** Tells people who just joined a group its name (caller holds the lock). */
  private async repostDmName(conv: Uuid): Promise<void> {
    const name = this.dmNames[conv]?.name
    if (!name || !this.core.hasGroup(conv)) return
    try {
      await this.sendLocked(conv, { v: 1, id: newConversationId(), ts: Date.now(), type: 'conv_name', name, repeat: true })
    } catch (e) {
      this.emit({ type: 'warning', message: `Could not share the group's name: ${(e as Error).message}` })
    }
  }

  /** Adds people to a group DM: their devices join now where they can (Welcome), else by themselves. */
  async addDmMembers(conv: Uuid, users: UserId[]): Promise<void> {
    for (const u of users) await this.api.addDmMember(conv, u)
    await this.reconcile()
    await this.exclusive(async () => {
      if (!this.core.hasGroup(conv)) return
      await this.addUsersNow(conv, users).catch((e) =>
        this.emit({ type: 'warning', message: `They'll join shortly (${(e as Error).message})` }),
      )
      await this.repostDmName(conv)
    })
  }

  /** Removes someone from a group DM I started; my client then removes their devices. */
  async removeDmMember(conv: Uuid, user: UserId): Promise<void> {
    await this.api.removeDmMember(conv, user)
    await this.reconcile()
  }

  /** Leaves a DM; the others' clients remove my devices from its group. */
  async leaveDm(conv: Uuid): Promise<void> {
    await this.api.leaveDm(conv)
    await this.reconcile()
  }

  async leaveSpace(space: Uuid): Promise<void> {
    await this.api.leaveSpace(space)
    await this.reconcile()
  }

  async kick(space: Uuid, user: UserId): Promise<void> {
    await this.api.kick(space, user)
    await this.reconcile()
    await this.exclusive(() => this.rotateSpaceKeyLocked(space, user))
    await this.reconcile()
  }

  /** Sets a space's symbol (pixel icon text), encrypted with the space key; null clears it. */
  async setSpaceIcon(space: Uuid, pixels: string | null): Promise<void> {
    const key = this.spaceKeys[space]
    await this.api.setSpaceIcon(space, pixels === null ? null : key ? await encryptName(key, 'icon', pixels) : pixels)
    await this.reconcile()
  }

  /** Sets a channel's colour (`#rrggbb`), or clears it, encrypted with the space key. */
  async setChannelColor(space: Uuid, channel: Uuid, color: string | null): Promise<void> {
    const key = this.spaceKeys[space]
    if (color !== null && !key) throw new Error('This space has no key to encrypt a colour with')
    await this.api.setChannelColor(space, channel, color === null ? null : await encryptName(key!, 'color', color))
    await this.reconcile()
  }

  /** Renames a space (or one of its channels), encrypting the name with the space key. */
  async rename(space: Uuid, name: string, channel?: Uuid): Promise<void> {
    const key = this.spaceKeys[space]
    if (channel) await this.api.renameChannel(space, channel, key ? await encryptName(key, 'channel', name) : name)
    else await this.api.renameSpace(space, key ? await encryptName(key, 'space', name) : name)
    await this.reconcile()
  }

  async deleteChannel(space: Uuid, conv: Uuid): Promise<void> {
    await this.api.deleteChannel(space, conv)
    await this.reconcile()
  }

  async deleteSpace(space: Uuid): Promise<void> {
    await this.api.deleteSpace(space)
    await this.reconcile()
  }

  async setRole(space: Uuid, user: UserId, role: Role): Promise<void> {
    await this.api.setRole(space, user, role)
    await this.reconcile()
  }

  messages(conv: Uuid, limit?: number): Promise<StoredMessage[]> {
    return this.storage.messages(conv, limit)
  }

  /** Hex code to compare out-of-band: equal on two devices = same keys, no MITM. */
  safetyCode(conv: Uuid): string | null {
    try {
      return this.core.epochAuthenticator(conv)
    } catch {
      return null
    }
  }

  /** MLS-derived media key for calls in this conversation at the current epoch. */
  callKey(conv: Uuid): { epoch: number; key: string } | null {
    return this.core.hasGroup(conv) ? this.core.callKey(conv) : null
  }

  hasGroup(conv: Uuid): boolean {
    return this.core.hasGroup(conv)
  }

  members(conv: Uuid): DeviceRef[] {
    return this.core.hasGroup(conv) ? this.core.members(conv) : []
  }

  /** Waits for all queued operations (tests). */
  idle(): Promise<void> {
    return this.exclusive(async () => undefined)
  }
}

/**
 * Human-meaningful membership changes from a commit: per user, whether they joined or
 * left the conversation, or added/removed a device while staying in it.
 */
/** A group DM name as kept: trimmed, at most 64 characters, empty for none. */
export function cleanDmName(name: string | null | undefined): string | null {
  const n = [...(name ?? '').trim()].slice(0, 64).join('')
  return n || null
}

export function membershipNotices(
  delta: MembershipDelta,
  membersAfter: DeviceRef[],
): { event: SystemEvent; user: UserId }[] {
  const out: { event: SystemEvent; user: UserId }[] = []
  const countAfter = (u: UserId) => membersAfter.filter((d) => d.user_id === u).length
  const users = (ds: DeviceRef[]) => [...new Set(ds.map((d) => d.user_id))]
  for (const u of users(delta.adds)) {
    const added = delta.adds.filter((d) => d.user_id === u).length
    out.push({ event: countAfter(u) > added ? 'device_added' : 'joined', user: u })
  }
  for (const u of users(delta.removes)) {
    out.push({ event: countAfter(u) > 0 ? 'device_removed' : 'left', user: u })
  }
  return out
}

/** Every current device of the given users, per the last sync. */
export function devicesOf(sync: SyncResponse, users: UserId[]): DeviceRef[] {
  return (sync.devices ?? [])
    .filter((u) => users.includes(u.user_id))
    .flatMap((u) => u.devices.map((d) => ({ user_id: u.user_id, device_id: d.device_id })))
}

export function linkToken(urlOrToken: string): string {
  urlOrToken = urlOrToken.split('#')[0]!
  const m = urlOrToken.trim().match(/\/link\/([A-Za-z0-9_-]+)\/?$/)
  return m?.[1] ?? urlOrToken.trim()
}

export function allConversations(sync: SyncResponse): ConversationInfo[] {
  return [...sync.spaces.flatMap((s) => s.channels), ...sync.conversations]
}
